import {
  createDesignerScreenInput,
  createDesignerScreenInputs,
  deleteDesignerScreenInput,
  deleteDesignerScreenInputs,
  getDesignerScreenInputUsage,
  listDesignerScreenInputs,
  updateDesignerScreenInput,
  type ScreenInputCreateRequest
} from "../../designer-screen-input-actions";
import { getSdInstance } from "../helpers";
import type { ToolHandlerGroup } from "../types";

/**
 * KOA-17362 — create/delete accept the WHOLE batch in one call.
 *
 * `ui_screen_input` used to be expanded server-side (`batch-fan-out.ts`) into one
 * call per item, and every single-item write persists through an un-awaited
 * `saveScreen`. Two items therefore raced two saves carrying different payloads
 * and the loser's input disappeared — deterministically the LATER one, which is
 * how a two-input batch reliably shipped only its first input. The batch entry
 * points below validate every item and then persist ONCE.
 *
 * The single-item shape is still accepted so direct callers (and any caller that
 * predates the batch schema) keep working unchanged.
 */
export const screenInputTools: ToolHandlerGroup = {
  sync: {
    ui_structure_list_screen_inputs: () => listDesignerScreenInputs(getSdInstance()),
    // KOA-18173 — which declared inputs anything on this screen actually READS. The
    // prune needs consumer evidence from the canvas, and a screen input's consumers
    // (action parameters, conditions, display bindings, filter variables) all live on
    // the declaring page. Read-only.
    ui_structure_screen_input_usage: () => getDesignerScreenInputUsage(getSdInstance())
  },
  // Every WRITE is async now: it resolves on the PERSIST, not on the in-memory
  // patch, so `ok` and `persisted` are answers rather than intentions.
  async: {
    ui_structure_create_screen_input: async (input) => {
      const batch: unknown = (input as { inputs?: unknown }).inputs;
      if (Array.isArray(batch)) {
        return await createDesignerScreenInputs(getSdInstance(), batch as ScreenInputCreateRequest[]);
      }
      return await createDesignerScreenInput(getSdInstance(), input);
    },
    ui_structure_update_screen_input: async (input) => await updateDesignerScreenInput(getSdInstance(), input),
    ui_structure_delete_screen_input: async (input) => {
      const batch: unknown = (input as { ids?: unknown }).ids;
      if (Array.isArray(batch)) {
        return await deleteDesignerScreenInputs(
          getSdInstance(),
          batch.filter((id: unknown): id is string => typeof id === "string")
        );
      }
      return await deleteDesignerScreenInput(getSdInstance(), input.id);
    }
  }
};
